// src/utils/platformDetector.js

function w() {
  return typeof window !== "undefined" ? window : undefined;
}
function nav() {
  const win = w();
  return win ? win.navigator : undefined;
}
function ua() {
  const n = nav();
  return (n && (n.userAgent || n.vendor || "")) || "";
}

/** iPadOS quirk: iPads can report "MacIntel" */
function _isIPadOSLike() {
  const n = nav();
  return !!(n && n.platform === "MacIntel" && n.maxTouchPoints > 1);
}

function _isIOSDevice() {
  const u = ua();
  return /iPad|iPhone|iPod/.test(u) || _isIPadOSLike();
}

/** True when running inside a Capacitor/Native wrapper (iOS) */
function _isCapacitorIOS() {
  const win = w();
  const cap = win && win.Capacitor;
  // Capacitor v5+: isNativePlatform(), getPlatform()
  const isNative = typeof cap?.isNativePlatform === "function" ? !!cap.isNativePlatform() : false;
  const plat = typeof cap?.getPlatform === "function" ? cap.getPlatform() : cap?.platform;
  return (isNative && (plat === "ios" || plat === "iphone" || plat === "ipad")) || plat === "ios";
}

/** Heuristic for WKWebView/in-app webview (NOT Safari/Chrome) */
function _isIOSInAppWebView() {
  const win = w();
  if (!win) return false;
  // WKWebView often exposes window.webkit.messageHandlers
  const isWK = !!win.webkit?.messageHandlers;
  // Facebook/Twitter in-app UAs include their names; still in-app
  const u = ua().toLowerCase();
  const isInAppUA = /fbios|fban|twitter|instagram|line\/|wkwebview/.test(u);
  return _isIOSDevice() && (isWK || isInAppUA);
}

/** iOS PWA "Add to Home Screen" (standalone). We treat as web, not native app. */
function _isIOSStandalonePWA() {
  const n = nav();
  const win = w();
  const standalone = !!(n && "standalone" in n && n.standalone); // iOS Safari only
  const displayModeStandalone =
    !!(win && win.matchMedia && win.matchMedia("(display-mode: standalone)").matches);
  return _isIOSDevice() && (standalone || displayModeStandalone);
}

/** Android native (Capacitor) */
function _isCapacitorAndroid() {
  const win = w();
  const cap = win && win.Capacitor;
  const isNative = typeof cap?.isNativePlatform === "function" ? !!cap.isNativePlatform() : false;
  const plat = typeof cap?.getPlatform === "function" ? cap.getPlatform() : cap?.platform;
  return (isNative && plat === "android") || plat === "android";
}

/** Public: keep your original exports, but tighten semantics. */
export function isIOSApp() {
  // Apple App Store binary (Capacitor/native) OR embedded WKWebView app shell.
  // IMPORTANT: This intentionally EXCLUDES Safari/Chrome and iOS PWAs.
  return _isCapacitorIOS() || _isIOSInAppWebView();
}

export function isAndroidApp() {
  return _isCapacitorAndroid();
}

/** Optional helpers if you need them elsewhere */
export function isIOSWeb() {
  // Safari/Chrome on iOS, including PWA standalone; safe to show billing link.
  return _isIOSDevice() && !isIOSApp();
}

export function isWeb() {
  return !isIOSApp() && !isAndroidApp();
}

/**
 * Billing policy helper:
 * - iOS native app (or in-app webview shell): NO direct billing links. Show site URL instead.
 * - Otherwise: direct link to your Billing page.
 *
 * Returns:
 *  {
 *    allowLink: boolean,
 *    linkHref?: string,           // e.g., "/account/billing" (your web route)
 *    altUrl?: string,             // e.g., "https://www.asklawly.com"
 *    reason: "ios_app" | "ok"
 *  }
 */
export function getBillingLinkPolicy({
  billingHref = "/account/billing",
  altUrl = "https://www.asklolly.com",
} = {}) {
  if (isIOSApp()) {
    return {
      allowLink: false,
      altUrl,
      reason: "ios_app",
    };
  }
  return {
    allowLink: true,
    linkHref: billingHref,
    reason: "ok",
  };
}

/** Optional: a single descriptor if you prefer switches */
export function getPlatformDescriptor() {
  if (isIOSApp()) return "ios_app";
  if (isAndroidApp()) return "android_app";
  if (isIOSWeb()) return "ios_web";
  return "web";
}
